import { useState } from "react";
import axios from "axios";
import pagarme from "pagarme/browser";
import { swalCustom } from "../../utils/functions";
import notify from "../UI/toast";
import { cardsApi } from "../../page/OrderResumePag/_services/api";
import { getCardBinAndBrand } from "../../page/OrderResumePag/_utils/card";
import { useUserData } from "../../hooks/UserContext";
import { useScreenTracking } from "../../hooks/useScreenTracking";
import { trackClick, trackCallbackMarketplace } from "../../utils/gtm";
import { tagIdFromLegacySuffix } from "../../utils/marketplaceTagIds";

// Maps a pagarme card field to the user-facing error message shown when
// validation fails. Order matches the original sequential check order.
const CARD_VALIDATION_MESSAGES = [
  ["card_holder_name", "Verifique o nome impresso no cartão."],
  ["card_number", "Verifique o número do cartão."],
  ["card_expiration_date", "Verifique a data de expiração do cartão."],
  ["card_cvv", "Verifique o código de segurança (CVV) do cartão."],
];

export function useCardManagement({
  cards,
  setCards,
  setRefreshCards,
  addresses,
}) {
  const [boxNewCard, setBoxNewCard] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user, setMainCard } = useUserData();
  const screenName = useScreenTracking();

  function track(title, contentType, suffix) {
    trackClick({
      title,
      contentType,
      screenName,
      tagId: tagIdFromLegacySuffix(suffix),
    });
  }

  function closeAddCardModal() {
    track("fechar-modal", "cadastrar-novo-cartao", "08");
    setBoxNewCard(false);
  }

  function openAddCardModal() {
    if (!addresses?.length) {
      notify("Você precisa de um endereço para cadastrar um cartão.", "error");
      return;
    }
    track("adicionar", "cartoes-cadastrados", "06");
    setBoxNewCard(true);
  }

  function onClickDelCard(uuidCard) {
    track("excluir-cartao", "cartoes-cadastrados", "06");

    if (cards.length === 1) {
      notify(
        "Este cartão não pode ser excluído enquanto não houver outro cartão cadastrado.",
        "error",
      );
      return;
    }

    swalCustom
      .fire({
        html: '<p class="fw-bold fs-5 mb-0">Deseja excluir este item?</p>',
        showCancelButton: true,
        confirmButtonText: "EXCLUIR",
        cancelButtonText: "CANCELAR",
        icon: "question",
      })
      .then(async (result) => {
        if (result.isConfirmed) {
          try {
            const apiResult = await cardsApi.delete(
              `/v1/customer/${user.cpf}/card/${uuidCard}`,
            );
            trackCallbackMarketplace({
              contentType: "excluir-cartao",
              screenName: `marketplace-${screenName}`,
              status: !apiResult.status ? "erro" : "sucesso",
            });

            if (!apiResult.status) {
              notify(apiResult.message, "error");
              return;
            }
            notify("Cartão removido com sucesso", "success");
            if (setRefreshCards) setRefreshCards(new Date());
          } catch (err) {
            notify("Erro ao deletar cartão.", "error");
          }
        } else if (result.dismiss) {
          track("cancelar-exclusao-cartao", "cadastrar-novo-cartao", "08");
        }
      });
  }

  async function saveNewCard(values) {
    setIsLoading(true);

    const cardPagarme = {
      card_number: values.card_number,
      card_holder_name: values.card_holder_name,
      card_expiration_date: values.card_expiration_date?.replace("/", ""),
      card_cvv: values.card_cvv,
    };
    const { card: validation } = pagarme.validate({ card: cardPagarme });

    for (const [field, message] of CARD_VALIDATION_MESSAGES) {
      if (!validation[field]) {
        setIsLoading(false);
        notify(message, "error");
        return false;
      }
    }

    const [expirationMonth, expirationYear] =
      values.card_expiration_date.split("/");
    const cardNumber = values.card_number.replaceAll(" ", "");

    let cardHash;
    try {
      const { data: tokenData, status } = await axios.request({
        method: "POST",
        url: `${process.env.REACT_APP_PAGARME_API}?appId=${process.env.REACT_APP_PAGARME_APPID}`,
        headers: {
          accept: "application/json",
          "content-type": "application/json",
        },
        data: {
          type: "card",
          card: {
            number: cardNumber,
            holder_name: values.card_holder_name,
            exp_month: expirationMonth,
            exp_year: expirationYear,
            cvv: values.card_cvv,
          },
        },
      });
      trackCallbackMarketplace({
        status: status === 200 ? "sucesso" : "erro",
        contentType: "cadastrar-novo-cartao",
        screenName: `marketplace-${screenName}`,
      });
      if (status !== 200) {
        setIsLoading(false);
        notify("Erro ao cadastrar dados do cartão.", "error");
        return;
      }
      cardHash = tokenData?.id;
    } catch (err) {
      console.error(err);
      setIsLoading(false);
      notify("Erro ao cadastrar dados do cartão.", "error");
      return;
    }

    const { bin, brand } = getCardBinAndBrand(values.card_number);
    const lastFourDigits = values.card_number?.slice(-4);
    const createdCard = {
      brand,
      bin,
      expiration_month: expirationMonth,
      expiration_year: expirationYear,
      holder_cpf: values.card_document,
      holder_name: values.card_holder_name,
      last_four_digits: lastFourDigits,
      main: cards.length === 0,
      hash: cardHash,
    };

    try {
      const { data, status } = await cardsApi.post(
        `/v1/customer/${user.cpf}/card`,
        {
          cardToken: cardHash,
          bin,
          brand,
          expirationMonth,
          expirationYear,
          holderName: createdCard.holder_name,
          lastFourDigits,
          main: createdCard.main,
        },
      );
      if (status !== 200) {
        notify("Erro ao cadastrar o cartão", "error");
        return;
      }
      notify("Cartão cadastrado com sucesso.", "success");
      createdCard.uuid = data?.data?.uuid;
      if (setCards) setCards([createdCard, ...cards]);
      setMainCard(createdCard);
      if (setRefreshCards) setRefreshCards(new Date());
      setBoxNewCard(false);
    } catch (error) {
      notify("Erro ao cadastrar o cartão", "error");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  return {
    boxNewCard,
    openAddCardModal,
    closeAddCardModal,
    saveNewCard,
    onClickDelCard,
    isLoading,
  };
}
